import { State, createPropertySelectors, Selector, Action, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';

import { computePeriodStartVideoSeconds, eaVideoPlayerStateDefaults } from './ea-video-player.utils';
import { IEaVideoPlayerSpeedForm, IEaVideoPlayerStateModel } from './ea-video-player.models';
import { SetCurrentVideoTimeSeconds, SetVideoCanBePlayed, SetVideoPlaybackError, SetVideoPlaying, ToggleMute } from './ea-video-player.actions';

import { GameState } from 'src/app/states/game/game.state';
import { IVidParam } from 'src/app/states/game/game.models';
import { EventState } from 'src/app/states/event/event.state';
import { ClockImpactType, IGameEvent, IGameEventDefinition } from 'src/app/states/event/event.models';
import { QueryParamsChanged } from 'src/services/query-params/query-params.actions';
import { INgxsFormStateValues } from 'src/app/states/store.models';

@Injectable()
@State<IEaVideoPlayerStateModel>({
    name: eaVideoPlayerStateDefaults.sliceName,
    defaults: eaVideoPlayerStateDefaults.defaults
})
export class EaVideoPlayerState {
    public static selectors = createPropertySelectors<IEaVideoPlayerStateModel>(EaVideoPlayerState);

    /**
     * Returns the vid param for the period that has been selected in the app
     * @param teamsById
     * @param game
     * @returns
     */
    @Selector([
        GameState.selectors.selectedPeriod,
        GameState.selectors.vidParams
    ])
    public static vidParamForSelectedPeriod(
        selectedPeriod: string | undefined,
        vidParams: IVidParam[] | undefined
    ): IVidParam | undefined {
        if (!selectedPeriod || !vidParams) {
            return;
        }

        return vidParams.find(param => `${param.period}` === selectedPeriod);
    }

    /**
     * Returns the video URL for the selected vidparam
     * @param vidParam
     * @returns
     */
    @Selector([
        EaVideoPlayerState.vidParamForSelectedPeriod
    ])
    public static videoUrl(
        vidParam: IVidParam | undefined
    ): string | undefined {
        if (!vidParam) {
            return;
        }

        return `https://${vidParam.rootUrl}/${vidParam.filename}`;
    }

    /**
     * Returns the video position (in seconds) where the selected period starts within the loaded
     * video file, anchored to the period's earliest clock-start game event (see
     * `computePeriodStartVideoSeconds`).
     *
     * Returns `undefined` until the selected period's game events have been fetched.
     */
    // eslint-disable-next-line complexity
    @Selector([
        EaVideoPlayerState.vidParamForSelectedPeriod,
        GameState.selectors.selectedPeriod,
        EventState.selectors.gameEvents,
        EventState.selectors.gameEventDefinitions
    ])
    public static selectedPeriodStartVideoSeconds(
        vidParam: IVidParam | undefined,
        selectedPeriod: string | undefined,
        gameEvents: IGameEvent[] | undefined,
        gameEventDefinitions: IGameEventDefinition[] | undefined
    ): number | undefined {
        const clockImpactByDefId: Record<string, ClockImpactType> = {};
        for (const definition of gameEventDefinitions ?? []) {
            clockImpactByDefId[definition.id] = definition.clockImpact;
        }

        return computePeriodStartVideoSeconds(
            vidParam?.frameRate ?? 0,
            parseInt(selectedPeriod ?? '1', 10),
            gameEvents ?? [],
            clockImpactByDefId
        );
    }

    @Selector([
        EaVideoPlayerState.selectors.videoSpeedForm
    ])
    public static videoSpeed(
        videoSpeedFormValues: INgxsFormStateValues<IEaVideoPlayerSpeedForm>
    ): number {
        return videoSpeedFormValues.model.videoSpeed;
    }

    // eslint-disable-next-line complexity
    @Action(QueryParamsChanged)
    private _queryParamsChanged(
        ctx: StateContext<IEaVideoPlayerStateModel>,
        { queryParams }: QueryParamsChanged
    ) {
        const state = ctx.getState();
        const patch: Partial<IEaVideoPlayerStateModel> = {};
        if (queryParams.timestamp && queryParams.timestamp !== state.queryParamsTimestamp) {
            patch.queryParamsTimestamp = queryParams.timestamp;
        }
        if (queryParams.eventid && queryParams.eventid !== state.queryParamsEventId) {
            patch.queryParamsEventId = queryParams.eventid;
        }
        if (Object.keys(patch).length > 0) {
            ctx.patchState(patch);
        }
    }

    @Action(SetVideoCanBePlayed)
    private _setVideoCanBePlayed(ctx: StateContext<IEaVideoPlayerStateModel>, { canBePlayed }: SetVideoCanBePlayed) {
        return ctx.patchState({
            videoCanBePlayed: canBePlayed
        });
    }

    @Action(ToggleMute)
    private _toggleMute(ctx: StateContext<IEaVideoPlayerStateModel>) {
        return ctx.patchState({
            videoMuted: !ctx.getState().videoMuted
        });
    }

    @Action(SetVideoPlaying)
    private _setVideoPlaying(ctx: StateContext<IEaVideoPlayerStateModel>, { isPlaying }: SetVideoPlaying) {
        return ctx.patchState({
            videoPlaying: isPlaying
        });
    }

    @Action(SetVideoPlaybackError)
    private _setVideoPlaybackError(ctx: StateContext<IEaVideoPlayerStateModel>, { error }: SetVideoPlaybackError) {
        return ctx.patchState({
            videoPlaybackError: error
        });
    }

    @Action(SetCurrentVideoTimeSeconds)
    private _setCurrentVideoTimeSeconds(ctx: StateContext<IEaVideoPlayerStateModel>, { seconds }: SetCurrentVideoTimeSeconds) {
        return ctx.patchState({
            currentVideoTimeSeconds: seconds
        });
    }
}
